import React from "react";
import { Label, Tag, cn } from "@jem-open/jem-ui";
import REPORT_TYPES from "../constants/reportTypes";
import { ReportType } from "../services/model/ReportType";
import { useUniqueId } from "../utils/useUniqueId";

interface ReportTypePickerProps {
  value: ReportType | null;
  onChange: (v: ReportType) => void;
  /** Report types to hide (e.g. Posting Sheet isn't available for regions). */
  hiddenTypes?: ReportType[];
}

const ReportTypePicker = ({
  value,
  onChange,
  hiddenTypes = [],
}: ReportTypePickerProps): JSX.Element => {
  const idBase = useUniqueId();
  const reportTypes = REPORT_TYPES.filter((rt) => !hiddenTypes.includes(rt.id));
  return (
    <div>
      <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
        Report type
      </Label>
      <div className="grid grid-cols-1 gap-sm">
        {reportTypes.map((rt) => {
          const RtIcon = rt.icon;
          const selected = value === rt.id;
          const descId = `${idBase}-${rt.id}-desc`;
          const audienceId = `${idBase}-${rt.id}-audience`;
          return (
            <button
              key={rt.id}
              type="button"
              onClick={() => onChange(rt.id)}
              className={cn(
                "text-left rounded-lg border p-md transition-colors",
                selected
                  ? "border-primary-brand-primary bg-secondary-pink-50"
                  : "border-greyscale-border bg-white hover:bg-neutral-50"
              )}
              aria-pressed={selected}
              aria-describedby={`${descId} ${audienceId}`}
            >
              <div className="flex items-start gap-sm">
                <div
                  className={cn(
                    "hidden sm:block rounded-md p-xs shrink-0",
                    selected
                      ? "bg-secondary-pink-100 text-secondary-pink-900"
                      : "bg-neutral-100 text-greyscale-text-body"
                  )}
                >
                  <RtIcon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-xs flex-wrap">
                    <span className="text-sm font-semibold text-greyscale-text-title">
                      {rt.label}
                    </span>
                    <Tag variant="drafted" size="sm">
                      {rt.output}
                    </Tag>
                  </div>
                  <p
                    id={descId}
                    className="text-xs text-greyscale-text-body mt-xxxs"
                  >
                    {rt.description}
                  </p>
                  <p
                    id={audienceId}
                    className="text-xs text-greyscale-text-caption mt-xxxs"
                  >
                    {rt.audience}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ReportTypePicker;
